import React, { useState, useRef, useEffect } from 'react';
import { useClarificationTypes, useWorkflowStatus } from '../hooks/useMasterData';
import { useNotification } from '../context/NotificationContext';
import SearchableDropdown from './SearchableDropdown';
import './BulkActionModal.css';

const BulkActionModal = ({ isOpen, onClose, onSave, selectedCount, users, isUnknownPage = false }) => {
  const { showError } = useNotification();
  const detailsTextareaRef = useRef(null);
  const { data: clarificationTypes, loading: clarificationTypesLoading } = useClarificationTypes(true);
  const { data: workflowStatuses, loading: workflowStatusesLoading } = useWorkflowStatus(true);
  const [formData, setFormData] = useState({
    assigneeId: '',
    reporterId: '',
    unknown: false,
    includeWorkflowStatus: false,
    workflowStatus: '',
    clarification: {
      clarificationType: '',
      details: '',
      status: 'OPEN',
      assigneeId: '',
      reporterId: '',
      assignee: 'ON-SHORE',
      reportee: 'OFF-SHORE',
      pageNumber: '',
      patientName: '',
      patientAccountNumber: '',
      insurance: ''
    },
    includeClarification: false
  });

  // Auto-expand textarea
  useEffect(() => {
    const textarea = detailsTextareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
    }
  }, [formData.clarification.details, formData.includeClarification]);

  if (!isOpen) return null;

  const handleChange = (field, value) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };
      
      // When "Mark as Unknown" is checked, automatically check "Add Clarification" (only if not on Unknown page)
      if (field === 'unknown' && value === true && !isUnknownPage) {
        newData.includeClarification = true;
      }
      
      return newData;
    });
  };

  const handleClarificationChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      clarification: {
        ...prev.clarification,
        [field]: value
      }
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate: If unknown is checked or clarification is included, ensure both clarificationType and details are provided
    if ((formData.unknown || formData.includeClarification)) {
      const clarificationType = formData.clarification.clarificationType?.trim();
      const details = formData.clarification.details?.trim();
      
      if (!clarificationType || !details) {
        // Show validation error using notification system
        showError('Please fill in both Clarification Type and Details');
        return;
      }
    }
    
    const payload = {};

    // Only add fields if they have values
    if (formData.assigneeId) {
      payload.assigneeId = formData.assigneeId;
    }
    
    if (formData.reporterId) {
      payload.reporterId = formData.reporterId;
    }
    
    // On Unknown page, we don't allow marking as known via bulk action
    // On Checks page, if "Mark as Unknown" checkbox is checked, set unknown to true
    if (!isUnknownPage && formData.unknown) {
      payload.unknown = true;
    }

    // Add workflow status if enabled and has a value
    if (formData.includeWorkflowStatus && formData.workflowStatus) {
      payload.workflowStatus = formData.workflowStatus;
    }

    // Add clarification if enabled and has required fields
    if (formData.includeClarification && formData.clarification.clarificationType && formData.clarification.details) {
      const clarification = { ...formData.clarification };
      
      // Remove empty fields
      Object.keys(clarification).forEach(key => {
        if (clarification[key] === '' || clarification[key] === undefined) {
          delete clarification[key];
        }
      });

      if (Object.keys(clarification).length > 0) {
        payload.clarification = clarification;
      }
    }

    onSave(payload);
    
    // Reset form
    setFormData({
      assigneeId: '',
      reporterId: '',
      unknown: false,
      includeWorkflowStatus: false,
      workflowStatus: '',
      clarification: {
        clarificationType: '',
        details: '',
        status: 'OPEN',
        assigneeId: '',
        reporterId: '',
        assignee: 'ON-SHORE',
        reportee: 'OFF-SHORE',
        pageNumber: '',
        patientName: '',
        patientAccountNumber: '',
        insurance: ''
      },
      includeClarification: false
    });
  };

  const handleCancel = () => {
    // Reset form
    setFormData({
      assigneeId: '',
      reporterId: '',
      unknown: false,
      includeWorkflowStatus: false,
      workflowStatus: '',
      clarification: {
        clarificationType: '',
        details: '',
        status: 'OPEN',
        assigneeId: '',
        reporterId: '',
        assignee: 'ON-SHORE',
        reportee: 'OFF-SHORE',
        pageNumber: '',
        patientName: '',
        patientAccountNumber: '',
        insurance: ''
      },
      includeClarification: false
    });
    onClose();
  };

  return (
    <div className="bulk-action-modal-overlay" onClick={handleCancel}>
      <div 
        className={`bulk-action-modal ${formData.includeClarification ? 'with-clarification' : 'without-clarification'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bulk-action-modal-header">
          <h2>Bulk Update Checks</h2>
          <button className="modal-close-btn" onClick={handleCancel}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        <div className="bulk-action-modal-content">
          <form onSubmit={handleSubmit}>
            <div className="form-section">
              <div className="form-section-header">
                <h3>Update Fields</h3>
                <span className="selected-count">{selectedCount} check(s) selected</span>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Assignee Name</label>
                  <SearchableDropdown
                    options={[
                      { value: '', label: 'Select Assignee' },
                      ...users.map(user => ({
                        value: user.userId || user.id,
                        label: user.firstName && user.lastName 
                          ? `${user.firstName} ${user.lastName}` 
                          : user.email || 'Unknown User'
                      }))
                    ]}
                    value={formData.assigneeId}
                    onChange={(value) => handleChange('assigneeId', value)}
                    placeholder="Select Assignee"
                    maxVisibleItems={5}
                  />
                </div>

                <div className="form-group">
                  <label>Reporter Name</label>
                  <SearchableDropdown
                    options={[
                      { value: '', label: 'Select Reporter' },
                      ...users.map(user => ({
                        value: user.userId || user.id,
                        label: user.firstName && user.lastName 
                          ? `${user.firstName} ${user.lastName}` 
                          : user.email || 'Unknown User'
                      }))
                    ]}
                    value={formData.reporterId}
                    onChange={(value) => handleChange('reporterId', value)}
                    placeholder="Select Reporter"
                    maxVisibleItems={5}
                  />
                </div>
              </div>

              {!isUnknownPage && (
                <div className="form-row">
                  <div className="form-group checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.unknown}
                        onChange={(e) => {
                          // On Checks page, checking means marking as Unknown (unknown = true)
                          handleChange('unknown', e.target.checked);
                        }}
                      />
                      <span>Mark as Unknown</span>
                    </label>
                  </div>

                  <div className="form-group checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.includeClarification || formData.unknown}
                        onChange={(e) => {
                          // If unknown is checked, don't allow unchecking clarification
                          if (formData.unknown && !e.target.checked) {
                            return;
                          }
                          handleChange('includeClarification', e.target.checked);
                        }}
                        disabled={formData.unknown}
                      />
                      <span>Add Clarification{formData.unknown && <span className="checkbox-hint"> (Auto selected)</span>}</span>
                    </label>
                  </div>
                </div>
              )}

              <div className="form-row workflow-status-row">
                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.includeWorkflowStatus}
                      onChange={(e) => {
                        handleChange('includeWorkflowStatus', e.target.checked);
                        if (!e.target.checked) {
                          handleChange('workflowStatus', '');
                        }
                      }}
                    />
                    <span>Update Workflow Status</span>
                  </label>
                </div>

                {formData.includeWorkflowStatus && (
                  <div className="form-group">
                    <SearchableDropdown
                      options={[
                        { value: '', label: 'Select Workflow Status' },
                        ...workflowStatuses.map(ws => ({
                          value: ws.code,
                          label: ws.name
                        }))
                      ]}
                      value={formData.workflowStatus}
                      onChange={(value) => handleChange('workflowStatus', value)}
                      placeholder="Select Workflow Status"
                      maxVisibleItems={5}
                      disabled={workflowStatusesLoading}
                    />
                  </div>
                )}
              </div>
            </div>

            {!isUnknownPage && (
              <div className="form-section">
                {(formData.includeClarification || formData.unknown) && (
                <div className="clarification-section">
                  <h4 className="clarification-title">Clarification Details</h4>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>Clarification Type *</label>
                      <SearchableDropdown
                        options={[
                          { value: '', label: 'Select Clarification Type' },
                          ...clarificationTypes.map(ct => ({
                            value: ct.code,
                            label: ct.name
                          }))
                        ]}
                        value={formData.clarification.clarificationType}
                        onChange={(value) => handleClarificationChange('clarificationType', value)}
                        placeholder="Select Clarification Type"
                        maxVisibleItems={5}
                        disabled={clarificationTypesLoading}
                      />
                    </div>

                    <div className="form-group">
                      <label>Status</label>
                      <select
                        value={formData.clarification.status}
                        onChange={(e) => handleClarificationChange('status', e.target.value)}
                      >
                        <option value="OPEN">Open</option>
                        <option value="RESOLVED">Resolved</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-row four-cols">
                    <div className="form-group">
                      <label>Page Number</label>
                      <input
                        type="text"
                        value={formData.clarification.pageNumber || ''}
                        onChange={(e) => handleClarificationChange('pageNumber', e.target.value)}
                        placeholder="Page number"
                      />
                    </div>
                    <div className="form-group">
                      <label>Patient Name</label>
                      <input
                        type="text"
                        value={formData.clarification.patientName || ''}
                        onChange={(e) => handleClarificationChange('patientName', e.target.value)}
                        placeholder="Patient name"
                      />
                    </div>
                    <div className="form-group">
                      <label>Patient Account Number</label>
                      <input
                        type="text"
                        value={formData.clarification.patientAccountNumber || ''}
                        onChange={(e) => handleClarificationChange('patientAccountNumber', e.target.value)}
                        placeholder="Patient account number"
                      />
                    </div>
                    <div className="form-group">
                      <label>Insurance</label>
                      <input
                        type="text"
                        value={formData.clarification.insurance || ''}
                        onChange={(e) => handleClarificationChange('insurance', e.target.value)}
                        placeholder="Insurance"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Details *</label>
                    <textarea
                      ref={detailsTextareaRef}
                      value={formData.clarification.details}
                      onChange={(e) => {
                        handleClarificationChange('details', e.target.value);
                        // Auto-expand
                        const textarea = e.target;
                        textarea.style.height = 'auto';
                        textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
                      }}
                      placeholder="Enter clarification details..."
                      rows="1"
                      className="expandable-textarea"
                      required={formData.includeClarification}
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Assignee</label>
                      <SearchableDropdown
                        options={[
                          { value: 'ON-SHORE', label: 'On-shore' },
                          { value: 'OFF-SHORE', label: 'Off-shore' }
                        ]}
                        value={formData.clarification.assignee || 'ON-SHORE'}
                        onChange={(value) => handleClarificationChange('assignee', value)}
                        placeholder="Select Type"
                        maxVisibleItems={2}
                      />
                    </div>

                    <div className="form-group">
                      <label>Reporter</label>
                      <SearchableDropdown
                        options={[
                          { value: 'OFF-SHORE', label: 'Off-shore' }
                        ]}
                        value={formData.clarification.reportee || 'OFF-SHORE'}
                        onChange={(value) => handleClarificationChange('reportee', value)}
                        placeholder="Select Type"
                        maxVisibleItems={1}
                      />
                    </div>
                  </div>
                </div>
              )}
              </div>
            )}

            <div className="bulk-action-modal-actions">
              <button type="button" className="btn-cancel" onClick={handleCancel}>
                Cancel
              </button>
              <button type="submit" className="btn-save">
                Update
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BulkActionModal;
